import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import {
  Sparkles, Plus, Minus, X, ArrowRight, ArrowLeft, Check, Store, Building2,
  Hotel, HeartPulse, GraduationCap, DoorOpen, Loader2, CheckCircle2, Wand2, ChevronLeft, ChevronRight, Pencil,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VG_ORANGE } from "../lib/brand";
import { getActiveHoliday, submitHolidayOrder } from "../lib/holidayApi";

const IDEAL_FOR = [
  { icon: Store, label: "Retail" }, { icon: Building2, label: "Corporate Offices" },
  { icon: Hotel, label: "Hospitality" }, { icon: HeartPulse, label: "Healthcare" },
  { icon: GraduationCap, label: "Education" }, { icon: DoorOpen, label: "Commercial Entrances" },
];
const SIZE_23 = "2' × 3'";
const SIZE_46 = "4' × 6'";
const PRICE_BY_SIZE = { [SIZE_23]: 30, [SIZE_46]: 120 };
const priceForSize = (s) => PRICE_BY_SIZE[s] ?? 0;
const money = (n) => "$" + Number(n || 0).toLocaleString("en-US");
const FONT_STACK = {
  "Alegreya Medium": "alegreya, Georgia, serif", Georgia: "Georgia, serif",
  "Times New Roman": "'Times New Roman', Times, serif", Arial: "Arial, Helvetica, sans-serif",
};
const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
const wordCount = (s) => (s.trim() ? s.trim().split(/\s+/).length : 0);
const numberForSize = (d, size) => {
  const m = (d.size_numbers || []).find((s) => s.size === size);
  return m ? m.number : (d.design_number || "");
};

function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(600);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((e) => setW(e[0].contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

function MatPreview({ design, message, font, colorHex, testId }) {
  const [ref, width] = useWidth();
  const z = design.personalization_zone || { x_pct: 50, y_pct: 80, width_pct: 74, align: "center", default_font_px: 46, min_font_px: 18, max_font_px: 60 };
  const fontPx = (Math.max(z.min_font_px, Math.min(z.max_font_px, z.default_font_px)) - 2) * (width / 600);
  return (
    <div ref={ref} className="relative w-full overflow-hidden rounded-lg border border-neutral-200 bg-neutral-100" data-testid={testId}>
      <img src={design.image} alt={design.name} className="block w-full" />
      <div className="absolute pointer-events-none font-semibold leading-tight break-words"
        style={{ left: `${z.x_pct}%`, top: `${z.y_pct}%`, width: `${z.width_pct}%`, transform: "translate(-50%,-50%)",
          textAlign: z.align || "center", color: colorHex, fontFamily: FONT_STACK[font] || "Georgia, serif",
          fontSize: `${fontPx}px`, textShadow: colorHex === "#ffffff" ? "0 1px 3px rgba(0,0,0,0.45)" : "none" }}
        data-testid={`${testId}-text`}>
        {message || "Your message here"}
      </div>
    </div>
  );
}

function Stepper({ qty, setQty, testId }) {
  return (
    <div className="inline-flex items-center rounded-md border border-neutral-300" data-testid={testId}>
      <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 text-neutral-600 hover:bg-neutral-50" data-testid={`${testId}-dec`}><Minus className="h-4 w-4" /></button>
      <span className="w-10 text-center text-sm font-semibold" data-testid={`${testId}-value`}>{qty}</span>
      <button onClick={() => setQty(qty + 1)} className="px-3 py-2 text-neutral-600 hover:bg-neutral-50" data-testid={`${testId}-inc`}><Plus className="h-4 w-4" /></button>
    </div>
  );
}

const emptyCustomer = {
  customer_type: "new", company: "", contact_name: "", email: "", phone: "",
  company_location: "", address1: "", address2: "", city: "", state: "", zip: "",
  ship_same: true,
  ship_company_location: "", ship_address1: "", ship_address2: "", ship_city: "", ship_state: "", ship_zip: "",
  needed_by_date: "", rep_name: "", notes: "",
};

const fmtAddr = (c, p) => [c[`${p}company_location`], c[`${p}address1`], c[`${p}address2`],
  [c[`${p}city`], c[`${p}state`], c[`${p}zip`]].filter(Boolean).join(", ")].filter(Boolean).join("\n");

const PERSONALIZATION_TYPES = ["Family Name", "Business Name", "Location Name", "Welcome Message", "Custom Message", "Logo + Text", "Other"];

export default function HolidayMatProgram() {
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [mode, setMode] = useState("build"); // build | review | confirmation
  const [entryway, setEntryway] = useState("");
  const [sizeFilter, setSizeFilter] = useState(null); // null | SIZE_23 | SIZE_46
  const [lines, setLines] = useState([]);
  const [customer, setCustomer] = useState(emptyCustomer);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const startedAt = useRef(Date.now());
  const lineSeq = useRef(0);
  const gallery = useRef(null);
  const scrollGallery = (d) => gallery.current?.scrollBy({ left: d * 320, behavior: "smooth" });

  useEffect(() => {
    getActiveHoliday().then((d) => { if (!d.program_active) setLoadErr(true); else setData(d); }).catch(() => setLoadErr(true));
  }, []);

  const collection = data?.collection;
  const designs = collection?.designs || [];
  const standard = designs.filter((d) => !d.personalization_enabled);
  const personalizable = designs.filter((d) => d.personalization_enabled);

  const pickEntry = (val) => { setEntryway(val); setSizeFilter(val === "single" ? SIZE_23 : SIZE_46); setTimeout(() => scrollToId("designs"), 80); };
  const totalMats = lines.reduce((n, l) => n + (l.quantity || 0), 0);
  const totalCost = lines.reduce((n, l) => n + priceForSize(l.size) * (l.quantity || 0), 0);

  const addLine = (design, size, quantity, personalization = null) => {
    lineSeq.current += 1;
    setLines((ls) => [...ls, { lineId: `L${lineSeq.current}`, design, size, quantity, personalization }]);
    toast.success(`${design.name} added to your request.`);
    setTimeout(() => scrollToId("request-summary"), 100);
  };
  const updateLine = (id, patch) => setLines((ls) => ls.map((l) => (l.lineId === id ? { ...l, ...patch } : l)));
  const removeLine = (id) => setLines((ls) => ls.filter((l) => l.lineId !== id));
  const setC = (k) => (e) => setCustomer((c) => ({ ...c, [k]: e.target.value }));

  const validate = () => {
    const er = {};
    if (lines.length === 0) er.lines = "Add at least one design to your request.";
    if (!customer.contact_name.trim()) er.contact_name = "Contact name is required.";
    if (!customer.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email)) er.email = "A valid email is required.";
    if (!customer.phone.trim()) er.phone = "Phone number is required.";
    if (!customer.address1.trim()) er.address1 = "Address is required.";
    if (!customer.city.trim()) er.city = "City is required.";
    if (!customer.state.trim()) er.state = "State is required.";
    if (!customer.zip.trim()) er.zip = "ZIP is required.";
    if (!customer.ship_same) {
      if (!customer.ship_address1.trim()) er.ship_address1 = "Address is required.";
      if (!customer.ship_city.trim()) er.ship_city = "City is required.";
      if (!customer.ship_state.trim()) er.ship_state = "State is required.";
      if (!customer.ship_zip.trim()) er.ship_zip = "ZIP is required.";
    }
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const goReview = () => {
    if (!validate()) { toast.error("Please complete the highlighted fields."); scrollToId("customer-info"); return; }
    setMode("review"); window.scrollTo(0, 0);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        collection_id: collection.id, collection_name: collection.name, entryway,
        customer,
        items: lines.map((l) => ({
          design_id: l.design.id, design_number: numberForSize(l.design, l.size), design_name: l.design.name,
          image: l.design.image, size: l.size, quantity: l.quantity,
          unit_price: priceForSize(l.size), personalization: l.personalization,
        })),
        hp_field: "", elapsed_ms: Date.now() - startedAt.current,
      };
      const res = await submitHolidayOrder(payload);
      setConfirmation({ ...res, lines }); setMode("confirmation"); window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your request. Please try again.");
    } finally { setBusy(false); }
  };

  if (loadErr) return (
    <div data-testid="holiday-inactive">
      <Seo title="Holiday Mat Program" description="Seasonal custom mat requests from van Gelder, Inc." />
      <PageHero title="Holiday Mat Program" subtitle="Our seasonal collection is not active right now. Please check back soon." testId="holiday-hero" />
      <div className="mx-auto max-w-3xl px-4 py-16 text-center text-neutral-600">The Holiday Mat Program is currently unavailable.</div>
    </div>
  );
  if (!data) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;

  if (mode === "confirmation" && confirmation) return (
    <div data-testid="holiday-confirmation">
      <Seo title="Request Submitted · Holiday Mat Program" description="Your Holiday Mat Request has been submitted." />
      <PageHero title="Holiday Mat Program" subtitle={collection.name} testId="holiday-hero" />
      <div className="mx-auto max-w-2xl px-4 py-16">
        <div className="rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-sm">
          <CheckCircle2 className="mx-auto h-14 w-14" style={{ color: VG_ORANGE }} />
          <h2 className="mt-4 font-heading text-2xl font-extrabold text-neutral-900">Your Holiday Mat Request Has Been Received</h2>
          <p className="mt-2 text-neutral-600">Thank you! Our team will review your request and follow up. This is a request receipt — it does not confirm the mats have entered production. A confirmation email is on its way to {confirmation.contact_name ? "you" : "your inbox"}.</p>
          <div className="mt-6 rounded-xl bg-neutral-50 p-5 text-left text-sm">
            <Row k="Request #" v={confirmation.reference} testId="confirmation-reference" />
            <Row k="Company" v={confirmation.company} />
            <Row k="Total mats" v={confirmation.total_mats} />
            <Row k="Estimated total" v={money((confirmation.lines || []).reduce((n, l) => n + priceForSize(l.size) * (l.quantity || 0), 0))} testId="confirmation-total-cost" />
            <Row k="Designs" v={(confirmation.lines || []).map((l) => `${l.design.name}${l.personalization?.enabled ? " (personalized)" : ""}`).join(", ")} />
            {confirmation.needed_by_date ? <Row k="Needed by (requested)" v={confirmation.needed_by_date} /> : null}
            <Row k="Ship to" v={confirmation.shipping_address} />
          </div>
        </div>
      </div>
    </div>
  );

  if (mode === "review") return (
    <div data-testid="holiday-review">
      <Seo title="Review Your Request · Holiday Mat Program" description="Review your Holiday Mat Request before submitting." />
      <PageHero title="Review Your Request" subtitle="Please confirm everything looks right, then submit." testId="holiday-hero" />
      <div className="mx-auto max-w-3xl px-4 py-12">
        <div className="mb-4 flex justify-between">
          <Button variant="outline" onClick={() => { setMode("build"); window.scrollTo(0, 0); }} data-testid="review-edit"><ArrowLeft className="mr-1.5 h-4 w-4" /> Edit request</Button>
        </div>
        <div className="space-y-3" data-testid="review-items">
          {lines.map((l) => (
            <div key={l.lineId} className="flex items-center gap-4 rounded-xl border border-neutral-200 bg-white p-4">
              <img src={l.design.image} alt="" className="h-16 w-16 rounded-md object-cover" />
              <div className="flex-1">
                <p className="font-heading font-bold text-neutral-900">{l.design.name}</p>
                <p className="text-xs text-neutral-500">{l.size}{!l.personalization?.enabled && ` · #${numberForSize(l.design, l.size) || "TBD"}`} · Qty {l.quantity} · <span className="font-semibold text-neutral-700">{money(priceForSize(l.size))} each</span></p>
                {l.personalization?.enabled && <p className="mt-0.5 text-xs font-medium" style={{ color: VG_ORANGE }}>{l.personalization.type ? `${l.personalization.type}: ` : "Personalized: "}“{l.personalization.message}” · {l.personalization.color}</p>}
              </div>
              <div className="text-right">
                {l.personalization?.preview_image && <img src={l.personalization.preview_image} alt="preview" className="h-16 w-24 rounded border border-neutral-200 object-cover" />}
                <p className="mt-1 font-heading text-sm font-extrabold text-neutral-900">{money(priceForSize(l.size) * l.quantity)}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between rounded-xl border border-neutral-200 bg-white p-4">
          <div>
            <span className="text-sm font-semibold text-neutral-700">Estimated Total</span>
            <p className="text-[11px] text-neutral-400">Before applicable tax &amp; shipping · final pricing confirmed after review</p>
          </div>
          <span className="font-heading text-2xl font-extrabold" style={{ color: VG_ORANGE }} data-testid="review-total-cost">{money(totalCost)}</span>
        </div>
        <div className="mt-6 grid gap-4 rounded-xl border border-neutral-200 bg-neutral-50 p-5 text-sm sm:grid-cols-2">
          <div><p className="font-semibold text-neutral-800">Contact</p><p className="text-neutral-600">{customer.company}<br/>{customer.contact_name}<br/>{customer.email}<br/>{customer.phone}</p></div>
          <div><p className="font-semibold text-neutral-800">Bill to</p><p className="whitespace-pre-line text-neutral-600">{fmtAddr(customer, "")}</p></div>
          <div><p className="font-semibold text-neutral-800">Ship to</p><p className="whitespace-pre-line text-neutral-600">{customer.ship_same ? "Same as billing address" : fmtAddr(customer, "ship_")}</p></div>
          {customer.needed_by_date && <div><p className="font-semibold text-neutral-800">Needed by (requested)</p><p className="text-neutral-600">{customer.needed_by_date}</p></div>}
          {customer.notes && <div><p className="font-semibold text-neutral-800">Notes</p><p className="text-neutral-600">{customer.notes}</p></div>}
        </div>
        <Button onClick={submit} disabled={busy} data-testid="submit-request" className="mt-8 h-12 w-full text-base text-white hover:opacity-90" style={{ backgroundColor: VG_ORANGE }}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Submit Holiday Mat Request <ArrowRight className="ml-2 h-5 w-5" /></>}
        </Button>
      </div>
    </div>
  );

  return (
    <div data-testid="holiday-page">
      <Seo title="Holiday Mat Program" description="Request seasonal custom entrance mats from van Gelder, Inc. Choose a design, size, and quantity, personalize eligible mats, and submit your request in minutes." />
      <PageHero title="Holiday Mat Program" subtitle={`${collection.name} — seasonal designs that make a lasting first impression. Choose your design, select a size and quantity, and submit your request in minutes.`} image={collection.hero_image} testId="holiday-hero" />

      {/* Entrance recommendation */}
      <section className="border-y border-neutral-200 bg-neutral-50">
        <div className="mx-auto max-w-6xl px-4 py-12">
          <h2 className="font-heading text-2xl font-extrabold text-neutral-900">What's Your Entryway?</h2>
          <p className="mt-1 text-neutral-600">We'll recommend a size — you can always browse all sizes.</p>
          <div className="mt-6 grid gap-4 sm:max-w-lg sm:grid-cols-2">
            {[["single", "Single", SIZE_23], ["double", "Double", SIZE_46]].map(([val, label, sz]) => {
              const active = entryway === val;
              return (
                <button key={val} onClick={() => pickEntry(val)} data-testid={`entryway-${val}`}
                  className={`flex items-center gap-3 rounded-xl border-2 bg-white p-5 text-left transition-all ${active ? "shadow-sm" : "border-neutral-200 hover:border-neutral-300"}`}
                  style={active ? { borderColor: VG_ORANGE } : {}}>
                  <DoorOpen className="h-8 w-8" style={{ color: active ? VG_ORANGE : "#9ca3af" }} />
                  <div><p className="font-heading font-bold text-neutral-900">{label}</p><p className="text-xs text-neutral-500">Recommends {sz}</p></div>
                  {active && <Check className="ml-auto h-5 w-5" style={{ color: VG_ORANGE }} />}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Browse designs */}
      <section id="designs" className="mx-auto max-w-7xl px-4 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-neutral-900">Browse Our Designs</h2>
            <p className="mt-1 text-neutral-600">{sizeFilter ? `Showing ${sizeFilter} (recommended for your entryway).` : "Select a design to add it to your request."}</p>
          </div>
          <div className="flex items-center gap-2">
            {sizeFilter && <Button variant="outline" onClick={() => setSizeFilter(null)} data-testid="view-all-sizes" className="border-[#ED8B00] text-[#ED8B00] hover:bg-orange-50">View All Designs &amp; Sizes</Button>}
            <div className="hidden gap-2 sm:flex">
              <button onClick={() => scrollGallery(-1)} aria-label="left" data-testid="design-carousel-prev" className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 hover:bg-neutral-50"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={() => scrollGallery(1)} aria-label="right" data-testid="design-carousel-next" className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 hover:bg-neutral-50"><ChevronRight className="h-5 w-5" /></button>
            </div>
          </div>
        </div>
        <div ref={gallery} data-testid="design-gallery" className="mt-6 flex snap-x gap-5 overflow-x-auto scroll-smooth pb-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {standard.map((d) => <DesignCard key={d.id} design={d} sizeFilter={sizeFilter} onAdd={addLine} />)}
        </div>
      </section>

      {/* Personalization workspace */}
      {personalizable.length > 0 && (
        <PersonalizeWorkspace designs={personalizable} onAdd={(design, size, quantity, pers) => addLine(design, size, quantity, pers)} />
      )}

      {/* Request summary */}
      <section id="request-summary" className="mx-auto max-w-4xl px-4 py-12">
        <h2 className="font-heading text-2xl font-extrabold text-neutral-900">Request Summary</h2>
        {errors.lines && <p className="mt-2 text-sm text-red-600" data-testid="error-lines">{errors.lines}</p>}
        <div className="mt-5 space-y-3" data-testid="request-lines">
          {lines.length === 0 && <div className="rounded-xl border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500">No designs added yet.</div>}
          {lines.map((l) => (
            <div key={l.lineId} data-testid={`request-line-${l.lineId}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-neutral-200 bg-white p-4">
              <img src={l.design.image} alt="" className="h-14 w-14 rounded-md object-cover" />
              <div className="min-w-[140px] flex-1">
                <p className="font-heading font-bold text-neutral-900">{l.design.name}</p>
                <p className="text-xs text-neutral-500">{l.size}{!l.personalization?.enabled && ` · #${numberForSize(l.design, l.size) || "TBD"}`} · <span className="font-semibold text-neutral-700" data-testid={`line-price-${l.lineId}`}>{money(priceForSize(l.size))} each</span></p>
                {l.personalization?.enabled && <p className="mt-0.5 text-xs font-medium" style={{ color: VG_ORANGE }} data-testid={`line-personalized-${l.lineId}`}><Wand2 className="mr-1 inline h-3 w-3" />“{l.personalization.message}”</p>}
              </div>
              <Stepper qty={l.quantity} setQty={(v) => updateLine(l.lineId, { quantity: v })} testId={`line-qty-${l.lineId}`} />
              <button onClick={() => removeLine(l.lineId)} data-testid={`line-remove-${l.lineId}`} className="ml-auto rounded-md p-2 text-neutral-400 hover:bg-red-50 hover:text-red-500"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-neutral-500">Total Mats</span>
          <span className="font-heading text-xl font-extrabold text-neutral-900" data-testid="summary-total-mats">{totalMats}</span>
        </div>
        <div className="mt-2 flex items-center justify-between border-t border-neutral-200 pt-3">
          <div>
            <span className="text-sm text-neutral-500">Estimated Total</span>
            <p className="text-[11px] text-neutral-400">Before applicable tax &amp; shipping · final pricing confirmed after review</p>
          </div>
          <span className="font-heading text-2xl font-extrabold" style={{ color: VG_ORANGE }} data-testid="summary-total-cost">{money(totalCost)}</span>
        </div>
        <Button onClick={() => scrollToId("customer-info")} disabled={lines.length === 0} data-testid="continue-to-information" className="mt-5 w-full text-white hover:opacity-90 sm:w-auto" style={{ backgroundColor: VG_ORANGE }}>Continue to Information <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
      </section>

      {/* Information */}
      <section id="customer-info" className="mx-auto max-w-3xl px-4 pb-16">
        <h2 className="font-heading text-2xl font-extrabold text-neutral-900">Your Information</h2>
        <div className="mt-6 flex gap-2" data-testid="customer-type-toggle">
          {[["existing", "Existing Customer"], ["new", "New Customer"]].map(([val, label]) => (
            <button key={val} onClick={() => setCustomer((c) => ({ ...c, customer_type: val }))} data-testid={`customer-type-${val}`}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${customer.customer_type === val ? "text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}
              style={customer.customer_type === val ? { backgroundColor: VG_ORANGE } : {}}>{label}</button>
          ))}
        </div>

        <h3 className="mt-8 font-heading text-lg font-bold text-neutral-900">Contact Information</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Company Name" error={errors.company}><Input value={customer.company} onChange={setC("company")} data-testid="cust-company" /></Field>
          <Field label="Contact Name" required error={errors.contact_name}><Input value={customer.contact_name} onChange={setC("contact_name")} data-testid="cust-contact" /></Field>
          <Field label="Email Address" required error={errors.email}><Input type="email" value={customer.email} onChange={setC("email")} data-testid="cust-email" /></Field>
          <Field label="Phone Number" required error={errors.phone}><Input value={customer.phone} onChange={setC("phone")} data-testid="cust-phone" /></Field>
          {customer.customer_type === "existing" && <Field label="REP Name"><Input value={customer.rep_name} onChange={setC("rep_name")} data-testid="cust-rep" /></Field>}
        </div>

        <h3 className="mt-8 font-heading text-lg font-bold text-neutral-900">Billing Address</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><Field label="Company / Location"><Input value={customer.company_location} onChange={setC("company_location")} data-testid="bill-company-location" /></Field></div>
          <div className="sm:col-span-2"><Field label="Address 1" required error={errors.address1}><Input value={customer.address1} onChange={setC("address1")} data-testid="bill-address1" /></Field></div>
          <div className="sm:col-span-2"><Field label="Address 2"><Input value={customer.address2} onChange={setC("address2")} data-testid="bill-address2" /></Field></div>
          <Field label="City" required error={errors.city}><Input value={customer.city} onChange={setC("city")} data-testid="bill-city" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="State" required error={errors.state}><Input value={customer.state} onChange={setC("state")} data-testid="bill-state" /></Field>
            <Field label="ZIP" required error={errors.zip}><Input value={customer.zip} onChange={setC("zip")} data-testid="bill-zip" /></Field>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-neutral-900">Shipping Address</h3>
        </div>
        <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-neutral-700" data-testid="ship-same-label">
          <input type="checkbox" checked={customer.ship_same} onChange={(e) => setCustomer((c) => ({ ...c, ship_same: e.target.checked }))}
            data-testid="ship-same-checkbox" className="h-4 w-4 rounded border-neutral-300" style={{ accentColor: VG_ORANGE }} />
          <span>Same as Billing Address</span>
        </label>
        {!customer.ship_same && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="shipping-fields">
            <div className="sm:col-span-2"><Field label="Company / Location"><Input value={customer.ship_company_location} onChange={setC("ship_company_location")} data-testid="ship-company-location" /></Field></div>
            <div className="sm:col-span-2"><Field label="Address 1" required error={errors.ship_address1}><Input value={customer.ship_address1} onChange={setC("ship_address1")} data-testid="ship-address1" /></Field></div>
            <div className="sm:col-span-2"><Field label="Address 2"><Input value={customer.ship_address2} onChange={setC("ship_address2")} data-testid="ship-address2" /></Field></div>
            <Field label="City" required error={errors.ship_city}><Input value={customer.ship_city} onChange={setC("ship_city")} data-testid="ship-city" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="State" required error={errors.ship_state}><Input value={customer.ship_state} onChange={setC("ship_state")} data-testid="ship-state" /></Field>
              <Field label="ZIP" required error={errors.ship_zip}><Input value={customer.ship_zip} onChange={setC("ship_zip")} data-testid="ship-zip" /></Field>
            </div>
          </div>
        )}

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Field label="Needed By Date (optional)"><Input type="date" value={customer.needed_by_date} onChange={setC("needed_by_date")} data-testid="cust-needed-by" /><p className="mt-1 text-xs text-neutral-500">Requested dates are not guaranteed and will be confirmed after review.</p></Field>
        </div>
        <div className="mt-4"><Field label="Order Notes / Special Instructions"><textarea value={customer.notes} onChange={setC("notes")} rows={3} data-testid="cust-notes" className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" /></Field></div>

        <Button onClick={goReview} data-testid="review-request" className="mt-8 h-12 w-full text-base text-white hover:opacity-90 sm:w-auto" style={{ backgroundColor: VG_ORANGE }}>Review Your Request <ArrowRight className="ml-2 h-5 w-5" /></Button>
      </section>

      {/* About */}
      <section className="border-t border-neutral-200 bg-neutral-50">
        <div className="mx-auto max-w-6xl px-4 py-12">
          <div className="mb-5 h-1 w-16" style={{ backgroundColor: VG_ORANGE }} />
          <h2 className="font-heading text-2xl font-extrabold text-neutral-900">About the Program</h2>
          <p className="mt-3 max-w-3xl leading-relaxed text-neutral-600">{collection.intro_copy}</p>
          <p className="mt-3 max-w-3xl leading-relaxed text-neutral-600" data-testid="charity-copy">
            50% of proceeds from the Holiday Mat Program will benefit K9s For Warriors, a van Gelder charitable partner dedicated to supporting veterans through highly trained Service Dogs.
            <br />
            <span className="font-semibold text-neutral-700">Learn more about K9s For Warriors: </span>
            <a href="https://k9sforwarriors.org" target="_blank" rel="noopener noreferrer"
              className="font-semibold underline hover:opacity-80" style={{ color: VG_ORANGE }} data-testid="charity-link">k9sforwarriors.org</a>
          </p>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-6">
            {IDEAL_FOR.map((it) => (
              <div key={it.label} className="flex flex-col items-center gap-2 rounded-lg border border-neutral-200 bg-white p-4 text-center">
                <it.icon className="h-6 w-6" style={{ color: VG_ORANGE }} /><span className="text-xs font-semibold text-neutral-700">{it.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function DesignCard({ design, sizeFilter, onAdd }) {
  const sizes = design.available_sizes || [SIZE_23, SIZE_46];
  const [size, setSize] = useState(sizeFilter || sizes[0]);
  const [qty, setQty] = useState(1);
  useEffect(() => { if (sizeFilter) setSize(sizeFilter); }, [sizeFilter]);
  const num = numberForSize(design, size);
  return (
    <div data-testid={`design-card-${design.id}`} className="group flex w-64 flex-shrink-0 snap-start flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm transition-all hover:shadow-md">
      <div className="h-44 overflow-hidden bg-neutral-100"><img src={design.image} alt={design.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /></div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-heading font-bold text-neutral-900">{design.name}</h3>
        {sizeFilter ? (
          <p className="mt-0.5 text-xs text-neutral-500">{size} · #{num || "TBD"}</p>
        ) : (
          <div className="mt-2 flex gap-1.5" data-testid={`card-size-${design.id}`}>
            {sizes.map((s) => (
              <button key={s} onClick={() => setSize(s)} data-testid={`card-size-${design.id}-${s === SIZE_23 ? "23" : "46"}`}
                className={`rounded border px-2 py-1 text-[11px] font-semibold ${size === s ? "border-[#ED8B00] text-[#ED8B00]" : "border-neutral-300 text-neutral-500"}`}>{s}</button>
            ))}
          </div>
        )}
        <div className="mt-3 flex items-center justify-between">
          <Stepper qty={qty} setQty={setQty} testId={`card-qty-${design.id}`} />
          <span className="font-heading text-lg font-extrabold text-neutral-900" data-testid={`card-price-${design.id}`}>{money(priceForSize(size))}</span>
        </div>
        <Button onClick={() => onAdd(design, size, qty)} data-testid={`design-add-${design.id}`} className="mt-3 text-white hover:opacity-90" style={{ backgroundColor: VG_ORANGE }}><Plus className="mr-1.5 h-4 w-4" /> Add to Request</Button>
      </div>
    </div>
  );
}

function PersonalizeWorkspace({ designs, onAdd }) {
  const [sel, setSel] = useState(designs[0]);
  const [message, setMessage] = useState("");
  const [ptype, setPtype] = useState("");
  const [color, setColor] = useState((sel.allowed_colors || [{ name: "Black", hex: "#111111" }])[0]);
  const [font, setFont] = useState((sel.allowed_fonts || Object.keys(FONT_STACK))[0]);
  const [size, setSize] = useState((sel.available_sizes || [SIZE_23])[0]);
  const [qty, setQty] = useState(1);
  const [agree, setAgree] = useState(false);
  const [saving, setSaving] = useState(false);
  const words = wordCount(message);
  const colors = sel.allowed_colors || [{ name: "Black", hex: "#111111" }, { name: "White", hex: "#ffffff" }];

  const pick = (d) => { setSel(d); setSize((d.available_sizes || [SIZE_23])[0]); setColor((d.allowed_colors || [{ name: "Black", hex: "#111111" }])[0]); setAgree(false); };

  const save = async () => {
    if (!ptype) return toast.error("Please choose a personalization type.");
    if (words < 1 || words > 10) return toast.error("Message must be 1–10 words.");
    if (!agree) return toast.error("Please accept the personalization disclaimer.");
    setSaving(true);
    let preview_image = "";
    try {
      if (document.fonts?.ready) await document.fonts.ready;
      const node = document.querySelector('[data-testid="personalize-preview"]');
      if (node) preview_image = (await html2canvas(node, { backgroundColor: "#ffffff", scale: 2, useCORS: true, logging: false })).toDataURL("image/png");
    } catch (e) { console.warn("preview capture failed", e); }
    onAdd(sel, size, qty, { enabled: true, type: ptype, message, font, color: color.name, color_hex: color.hex, approved: true, preview_image });
    setMessage(""); setPtype(""); setAgree(false); setSaving(false);
  };

  return (
    <section className="border-y border-neutral-200 bg-neutral-50">
      <div className="mx-auto max-w-6xl px-4 py-12">
        <div className="flex items-center gap-2"><Sparkles className="h-5 w-5" style={{ color: VG_ORANGE }} /><h2 className="font-heading text-2xl font-extrabold text-neutral-900">Personalize a Design</h2></div>
        <p className="mt-1 text-neutral-600">Customize one of our personalization-eligible designs. This is a placement preview, not final artwork.</p>
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
          <div><MatPreview design={sel} message={message} font={font} colorHex={color.hex} testId="personalize-preview" /></div>
          <div className="space-y-4 rounded-xl border border-neutral-200 bg-white p-5" data-testid="personalize-panel">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Design</label>
              <div className="grid grid-cols-2 gap-2">
                {designs.map((d) => (
                  <button key={d.id} onClick={() => pick(d)} data-testid={`personalize-select-${d.id}`}
                    className={`overflow-hidden rounded-lg border text-left ${sel.id === d.id ? "border-[#ED8B00] ring-1 ring-[#ED8B00]" : "border-neutral-200"}`}>
                    <img src={d.image} alt={d.name} className="h-14 w-full object-cover" />
                    <span className="block truncate px-2 py-1 text-[11px] font-semibold text-neutral-700">{d.name}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Size</label>
              <select value={size} onChange={(e) => setSize(e.target.value)} data-testid="personalize-size" className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm">
                {(sel.available_sizes || [SIZE_23]).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <p className="mt-1.5 text-sm font-bold text-neutral-900" data-testid="personalize-price">{money(priceForSize(size))} <span className="text-xs font-normal text-neutral-500">each · personalization included at no extra cost</span></p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Personalization Type</label>
              <select value={ptype} onChange={(e) => setPtype(e.target.value)} data-testid="personalize-type" className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm">
                <option value="">Select a type…</option>
                {PERSONALIZATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Message</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} data-testid="personalize-message" className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" placeholder="e.g. Welcome by the Griffin Family" />
              <p className={`mt-1 text-xs ${words === 0 || (words >= 1 && words <= 10) ? "text-neutral-500" : "text-red-600"}`} data-testid="personalize-wordcount">Please limit your message to 1–10 words. ({words})</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Font</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(FONT_STACK).map((f) => (
                  <button key={f} onClick={() => setFont(f)} data-testid={`personalize-font-${f.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`rounded-md border px-3 py-2 text-sm ${font === f ? "border-[#ED8B00] ring-1 ring-[#ED8B00] text-neutral-900" : "border-neutral-300 text-neutral-600"}`}
                    style={{ fontFamily: FONT_STACK[f] }}>{f}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Text Color</label>
              <div className="flex gap-2">
                {colors.map((cl) => (
                  <button key={cl.name} onClick={() => setColor(cl)} data-testid={`personalize-color-${cl.name.toLowerCase()}`}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${color.name === cl.name ? "border-[#ED8B00] ring-1 ring-[#ED8B00]" : "border-neutral-300"}`}>
                    <span className="h-4 w-4 rounded-full border border-neutral-300" style={{ backgroundColor: cl.hex }} />{cl.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Quantity</label>
              <Stepper qty={qty} setQty={setQty} testId="personalize-qty" />
            </div>
            <label className="flex items-start gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="personalize-disclaimer" className="mt-0.5" />
              <span>I understand that this preview is for concept and placement reference only. Final artwork will be reviewed and approved before production. Font style, color, sizing, and placement will be reproduced as closely as possible to the approved preview, but minor adjustments may be made during final artwork preparation.</span>
            </label>
            <Button onClick={save} disabled={saving} data-testid="save-personalization" className="w-full text-white hover:opacity-90" style={{ backgroundColor: VG_ORANGE }}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Save Personalization <Check className="ml-1.5 h-4 w-4" /></>}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

const Row = ({ k, v, testId }) => (
  <div className="flex justify-between gap-4 border-b border-neutral-200 py-2 last:border-0"><span className="text-neutral-500">{k}</span><span className="text-right font-semibold text-neutral-900" data-testid={testId}>{v || "—"}</span></div>
);
const Field = ({ label, required, error, children }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">{label}{required && <span className="text-red-500"> *</span>}</label>
    {children}{error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);
